<template>
    <div class="card card-height">
        <div class="table-responsive">
            <table class="table">
                <thead>
                    <tr>
                        <th>Particulars</th>
                        <th class="text-end">Amount</th>
                        <th class="text-end">Total</th>
                    </tr>
                </thead>
                <tbody v-for="(item, i) in service" :key="i">
                    <tr class="bg-blue-light">
                        <td colspan="3">
                            <span class="text-decoration-underline">{{ item.name }}</span>
                        </td>
                    </tr>
                    <tr
                        v-for="(el, index) in item.child"
                        :key="index"
                        :class="{ 'return-row': el.is_return }"
                    >
                        <td>{{ el.name }}</td>
                        <td class="text-end">{{ formatSigned(el.total_amount) }}</td>
                        <td v-if="index === 0" :rowspan="item.child.length + 1" class="text-end fw-bold">
                            {{ formatSigned(childTotal(item.child)) }}
                        </td>
                    </tr>
                </tbody>
            </table>

            <p v-if="!service.length" class="text-center mt-5">No bill found</p>

            <div v-if="service.length" class="row py-2">
                <div class="col-md-6 d-flex align-items-center">
                    <span v-if="isDue" class="py-1 px-2 due">Due</span>
                    <span v-if="!isDue" class="py-1 px-2 paid">Paid</span>
                </div>
                <div class="col-md-6 table-responsive">
                    <table class="table">
                        <tbody>
                            <tr>
                                <td class="text-end fw-bold">Total Amount</td>
                                <td class="text-end">{{ commaFormat(totalAfterReturn) }}</td>
                            </tr>
                            <tr>
                                <td class="text-end fw-bold">(-) Bill Discount</td>
                                <td class="text-end">{{ commaFormat(total.discount) }}</td>
                            </tr>
                            <tr>
                                <td class="text-end fw-bold">(-) Settlement Discount</td>
                                <td class="text-end">{{ commaFormat(total.settlement_discount) }}</td>
                            </tr>
                            <tr>
                                <td class="text-end fw-bold">Net Bill Amount</td>
                                <td class="text-end">{{ commaFormat(netAmount) }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <div v-if="receiptGenerals.length" class="table-responsive">
                <table class="table">
                    <thead>
                        <tr>
                            <th>MR Date &amp; Time</th>
                            <th>MR No</th>
                            <th>Mode of payment</th>
                            <th>MoP Ref.</th>
                            <th>Receipt by</th>
                            <th class="text-end">Receipt Amount</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(item, index) in receiptGenerals" :key="index">
                            <td>{{ formatDateWithTime(item.created_at) }}</td>
                            <td>{{ item.voucher_no }}</td>
                            <td>{{ headName(item) }}</td>
                            <td>{{ item.receipt_reference }}</td>
                            <td>{{ userName(item) }}</td>
                            <td class="text-end">{{ commaFormat(item.total_paid_amount) }}</td>
                        </tr>
                        <tr>
                            <td colspan="5" class="text-end">Total</td>
                            <td class="text-end">{{ commaFormat(totalPaidAmount) }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <div class="row py-2">
            <div v-if="isNotSettled" class="col-md-5">
                <MoneyReceiptForm :formData="formData" :grand-total="netPayable"/>
            </div>
            <div v-if="service.length" class="offset-md-2 col-md-5">
                <table class="table">
                    <tbody>
                        <tr>
                            <td class="text-end fw-bold">Net Bill Amount</td>
                            <td class="text-end">{{ commaFormat(netAmount) }}</td>
                        </tr>
                        <tr>
                            <td class="text-end fw-bold">Total Paid</td>
                            <td class="text-end">{{ commaFormat(totalPaidAmount) }}</td>
                        </tr>
                        <tr>
                            <td class="text-end fw-bold">Due / Return Amount</td>
                            <td class="text-end">{{ commaFormat(formData.bill_amount) }}</td>
                        </tr>
                        <tr v-if="isNotSettled">
                            <td class="text-end fw-bold">
                                Discount / Round up Adj
                                <input
                                    v-model.trim="discountPercent"
                                    type="number"
                                    class="form-control form-control-sm discount-adj ms-auto text-end"
                                    placeholder="%"
                                    :min="0"
                                >
                            </td>
                            <td class="text-end">
                                <input
                                    v-model.trim="discountAmount"
                                    type="number"
                                    class="form-control form-control-sm discount-adj ms-auto text-end mt-1"
                                    placeholder="amount"
                                    :min="0"
                                >
                            </td>
                        </tr>
                        <tr v-if="discountAmount > 0">
                            <td class="text-end fw-bold">Select discount head</td>
                            <td>
                                <v-select
                                    placeholder="Select Discount Head"
                                    label="name"
                                    v-model="formData.discount_head_id"
                                    :options="salesAccountHeads"
                                    :reduce="name => name.id"
                                />
                            </td>
                        </tr>
                        <tr>
                            <td class="text-end fw-bold">Net Payable</td>
                            <td class="text-end">{{ commaFormat(netPayable) }}</td>
                        </tr>
                        <tr>
                            <td class="text-end fw-bold">Paid Amount</td>
                            <td class="text-end">{{ commaFormat(formData.payment) }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <div class="col-12 d-flex justify-content-center mt-2">
            <button
                v-if="isNotSettled"
                class="btn btn-success"
                :disabled="disableStlBtn"
                @click="handleSettlement"
            >
                Add Final Settlement
            </button>
            <RouterLink
                v-if="isSettled"
                class="btn btn-success"
                :to="{ name: 'ipd-statement-edit', params: editParams, query: editQuery }"
            >
                Update Final Settlement
            </RouterLink>
        </div>
    </div>
</template>

<script setup>
import MoneyReceiptForm from '@/components/molecule/company/hospital/ipd-station/MoneyReceiptForm.vue';
import { computed, inject, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import figureFormatter from '@/services/utils/figureFormatter';
import useDate from '@/services/utils/day';
import handleHospital from '@/services/modules/hospital';
import handlePurchase from '@/services/modules/purchase';

const props = defineProps({
    service: {
        type: Array,
        default: () => [],
    },
    total: {
        type: Object,
        default: () => ({}),
    },
    receiptGenerals: {
        type: Array,
        default: () => [],
    },
    patientDetails: {
        type: Object,
        default: () => ({}),
    },
});

const emit = defineEmits(['onSettled']);

const $route = useRoute();
const showError = inject('showError');
const showSuccess = inject('showSuccess');
const { commaFormat } = figureFormatter();
const { formatDateWithTime } = useDate();
const { storeSettlement } = handleHospital();
const { getAccountHeadBySlag } = handlePurchase();

const discountPercent = ref(null);
const discountAmount = ref(null);
const disableStlBtn = ref(false);
const salesAccountHeads = ref([]);
const formData = ref({
    date: new Date().toISOString().split('T')[0],
    company_id: $route.params.companyId,
    contact_profile_id: null,
    ipd_register_id: null,
    receivable_account_head_id: null,
    mop_account_head_id: null,
    discount_amount: 0,
    discount_head_id: null,
    reference: '',
    payment: 0,
    bill_amount: 0,
});

const isNotSettled = computed(() => props.patientDetails.settlement_status === 'not_settled');

const isSettled = computed(() => props.patientDetails.settlement_status === 'settled');

const editParams = computed(() => {
    return {
        companyId: $route.params.companyId,
        moduleId: $route.params.moduleId,
        menuId: $route.params.menuId,
        pageId: $route.params.pageId,
    };
});

const editQuery = computed(() => {
    return Object.assign({}, $route.query, { id: props.patientDetails.id });
});

const totalPaidAmount = computed(() => {
    let sum = 0;
    for (const item of props.receiptGenerals) {
        sum += Number(item.total_paid_amount);
    }
    return sum;
});

const returnAmount = computed(() => Number(props.total.return_amount || 0));

const totalAfterReturn = computed(() => Number(props.total.amount || 0) - returnAmount.value);

const netAmount = computed(() => {
    const discount = Number(props.total.discount || 0);
    const settlementDiscount = Number(props.total.settlement_discount || 0);
    return totalAfterReturn.value - discount - settlementDiscount;
});

const netPayable = computed(() => (netAmount.value - totalPaidAmount.value) - Number(discountAmount.value || 0));

const isDue = computed(() => netAmount.value > totalPaidAmount.value);

const childTotal = (child) => {
    let sum = 0;
    for (const item of child) {
        sum += Number(item.total_amount);
    }
    return sum;
};

const formatSigned = (value) => {
    if (value < 0) return `(${commaFormat(Math.abs(value))})`;
    return commaFormat(value);
};

const headName = (item) => {
    if (!item.account_head) return '';
    return item.account_head.name;
};

const userName = (item) => {
    if (!item.created_user) return '';
    return item.created_user.name;
};

const resolveDiscountHead = () => {
    if (!(discountAmount.value > 0)) return null;
    if (!salesAccountHeads.value.length) return null;
    return salesAccountHeads.value[0].id;
};

watch(discountPercent, () => {
    if (Number(discountPercent.value) === 0) {
        discountAmount.value = 0;
        return;
    }
    discountAmount.value = ((netAmount.value - totalPaidAmount.value) * Number(discountPercent.value)) / 100;
});

watch(netAmount, () => {
    formData.value.bill_amount = netAmount.value - totalPaidAmount.value;
}, { immediate: true });

watch(netPayable, (newValue) => {
    formData.value.payment = newValue;
}, { immediate: true });

watch(discountAmount, (newValue) => {
    if (newValue < 0) {
        showError('Discount can not be less than 0');
        discountAmount.value = 0;
    }
    formData.value.discount_amount = parseFloat(discountAmount.value);
    formData.value.discount_head_id = resolveDiscountHead();
});

watch(() => props.patientDetails, (newValue) => {
    if (!newValue) return;
    formData.value.contact_profile_id = newValue.contact_profile_id;
    formData.value.ipd_register_id = newValue.id;
}, { deep: true, immediate: true });

const handleSettlement = () => {
    disableStlBtn.value = true;
    storeSettlement(formData.value.ipd_register_id, formData.value).then(res => {
        if (!res.status) {
            showError(res.message);
            disableStlBtn.value = false;
            return;
        }
        showSuccess(res.message);
        emit('onSettled');
    }).catch(() => {
        showError('Something went wrong');
        disableStlBtn.value = false;
    });
};

onMounted(() => {
    const companyQuery = `?company_id=${$route.params.companyId}`;
    getAccountHeadBySlag('discount_account', companyQuery).then(res => {
        if (res.data) salesAccountHeads.value = res.data;
    });
});
</script>

<style scoped>
.paid {
    background-color: #27b032;
    color: #fff;
    font-size: 24px;
    border-radius: 15px;
}
.due {
    background-color: #000;
    color: #fff;
    font-size: 24px;
    border-radius: 15px;
}
.discount-adj {
    width: 60%;
}
.return-row td {
    background-color: #fde8e8;
    color: #b02a2a;
}
</style>
